/**
 * /accessibility — AODA accessibility statement.
 *
 * AODA applies to Blockchain PMO Inc. as an Ontario corporation with at
 * least one employee. We're not required to be fully WCAG 2.0 AA
 * conformant on day one of a pre-launch product, but we ARE required to
 * publish a statement and have a multi-year plan. This is the statement.
 *
 * Important: do NOT claim full WCAG 2.0 AA conformance until we have
 * actually audited the product. False accessibility claims create their
 * own liability.
 */
import { BrandedLayout } from '@/components/BrandedLayout';

export default function Accessibility() {
  return (
    <BrandedLayout>
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6 prose prose-sm">
        <h1 className="text-2xl font-semibold tracking-tight">
          Accessibility Statement
        </h1>
        <p className="text-sm text-muted-foreground">
          Last updated: 2026-05-04
        </p>

        <p>
          Blockchain PMO Inc., the operator of PharmaSync at
          pharma-sync.ca, is committed to providing a website and
          application that is accessible to the widest possible audience,
          regardless of ability or technology.
        </p>

        <h2 className="text-lg font-medium">Our commitment</h2>
        <p>
          We strive to conform to the <strong>Web Content Accessibility
          Guidelines (WCAG) 2.0 Level AA</strong> as required under the{' '}
          <em>Accessibility for Ontarians with Disabilities Act</em>{' '}
          (AODA). PharmaSync is currently a pre-launch product and we are
          working toward full WCAG 2.0 Level AA conformance for both our
          marketing website and the PharmaSync application before general
          availability.
        </p>

        <h2 className="text-lg font-medium">
          Current accessibility posture
        </h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <strong>Marketing site</strong> (pharma-sync.ca): under
            development; accessibility considerations include semantic
            HTML, sufficient colour contrast, keyboard navigation,
            descriptive alt text, and screen-reader-friendly structure.
          </li>
          <li>
            <strong>PharmaSync application</strong>: built with semantic
            HTML and ARIA attributes where needed; supports keyboard
            navigation; sufficient colour contrast in the default theme;
            screen-reader-tested for primary user flows.
          </li>
        </ul>
        <p>
          We have not yet engaged a third-party accessibility audit. We
          expect to do so before general availability.
        </p>

        <h2 className="text-lg font-medium">Known limitations</h2>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            Some advanced calendar and scheduling interactions in the
            PharmaSync application are visually-driven and may be
            challenging with a screen reader. We are working to improve
            these flows.
          </li>
          <li>
            Marketing site is in active development; some pages may not
            yet meet our target conformance level.
          </li>
        </ul>

        <h2 className="text-lg font-medium">Feedback</h2>
        <p>
          We welcome feedback on accessibility. If you encounter a
          barrier using our website or application, or if you have
          suggestions, please email{' '}
          <a href="mailto:hello@pharma-sync.ca" className="underline">
            hello@pharma-sync.ca
          </a>
          . Include the URL or screen where you encountered the issue
          and, if you are comfortable, the assistive technology you are
          using. We aim to respond within 2 business days.
        </p>
        <p>
          You may also request information or content in an accessible
          format. There is no charge for this service.
        </p>

        <h2 className="text-lg font-medium">
          Multi-year accessibility plan
        </h2>
        <p>
          We are documenting a multi-year accessibility plan as required
          under AODA. The plan will be published on this page once
          finalized. It will cover:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            Targets for WCAG 2.0 Level AA conformance milestones.
          </li>
          <li>
            Procurement criteria for accessible third-party tools.
          </li>
          <li>Training for our team.</li>
          <li>Customer feedback channels and review cadence.</li>
        </ul>

        <h2 className="text-lg font-medium">Contact</h2>
        <p>
          For accessibility feedback or to request content in an
          accessible format:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>
            <strong>Email:</strong>{' '}
            <a href="mailto:hello@pharma-sync.ca" className="underline">
              hello@pharma-sync.ca
            </a>
          </li>
          <li>
            <strong>Mail:</strong> Blockchain PMO Inc., Ontario, Canada
          </li>
        </ul>
      </div>
    </div>
    </BrandedLayout>
  );
}
